import { useAppSelector } from "hooks/redux";
import { BookingPlayerType } from "api/rpc/2024-04/customer/facility";
import { useParams } from "react-router";

type BookingSetting = {
  disableSingleBookings: boolean,
  disableTwosomeBookings: boolean,
  disableThreesomeBookings: boolean,
  disableFoursomeBookings: boolean,
  golferAmount: number;
};

interface IParams {
  clientShortName: string;
  rwg_token?: string;
}

interface IClientPortalURLParams {
  date?: string;
  waitlist_reference_id?: string;
  rwg_token?: string;
}

export function useClientPortal() {
  const { clientShortName } = useParams<IParams>();
  const clientPortalStore = useAppSelector(store => store.clientPortalStore);

  /**
   * Determines if what player numbers for the booking are disabled for the selected tee time
   *
   *
   * @param singleBookings single_bookings value from the facility_tee_sheet_settings table ("allow", "allow_within_group", "none")
   * @param twosomeBookings twosome_bookings value from the facility_tee_sheet_settings table ("allow", "allow_within_group", "none")
   * @param threesomeBookings threesome_bookings value from the facility_tee_sheet_settings table ("allow", "allow_within_group", "none")
   * @param foursomeBookings foursome_bookings value from the facility_tee_sheet_settings table ("allow", "allow_within_group", "none")
   * @param quantityRemaining The remaining number of slots on the tee time
   * @returns {BookingSetting} An object with a disable boolean value and a default golfer amount
   */
  function getDisabledBookings(singleBookings: BookingPlayerType, twosomeBookings: BookingPlayerType, threesomeBookings: BookingPlayerType, foursomeBookings: BookingPlayerType, quantityRemaining: number, teeSize = 4) {
    const bookingSettings: BookingSetting = {
      disableSingleBookings: false,
      disableTwosomeBookings: false,
      disableThreesomeBookings: false,
      disableFoursomeBookings: false,
      golferAmount: 1,
    };
    const fullyOpen = quantityRemaining >= teeSize;
    if ((singleBookings === "allow_within_group" && fullyOpen) || singleBookings === "none") {
      bookingSettings.disableSingleBookings = true;
    }
    if ((twosomeBookings === "allow_within_group" && fullyOpen) || twosomeBookings === "none") {
      bookingSettings.disableTwosomeBookings = true;
    }
    if ((threesomeBookings === "allow_within_group" && fullyOpen) || threesomeBookings === "none") {
      bookingSettings.disableThreesomeBookings = true;
    }
    if (foursomeBookings === "none") {
      bookingSettings.disableFoursomeBookings = true;
    }

    if (bookingSettings.disableSingleBookings) {
      bookingSettings.golferAmount = 2;
      if (bookingSettings.disableTwosomeBookings) {
        bookingSettings.golferAmount = 3;
        if (bookingSettings.disableThreesomeBookings) {
          bookingSettings.golferAmount = 4;
        }
      }
    }

    return bookingSettings;
  }

  /**
   *
   * @returns {string} URL that directs the user to the Client Portal home page
   */
  function getHomePageUrl(): string {
    const facilityShortName = clientPortalStore?.selectedFacility
      ? `/${clientPortalStore?.selectedFacility?.short_name}`
      : "";
    return `/portal/${clientShortName}/teetimes`.concat(facilityShortName);
  }

  /**
   *
   * @example {date: "2024-04-01", waitlist_reference_id: 1}
   * @returns Object containing the URL params
   */
  function getURLParams(): IClientPortalURLParams {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const date = urlParams.get("date");
    const waitlist_reference_id = urlParams.get("waitlist_reference_id");
    const rwg_token = urlParams.get("rwg_token");
    return { date: date, waitlist_reference_id: waitlist_reference_id, rwg_token: rwg_token };
  }

  /**
   * @description Returns boolean values showPaymentDropdown & showPrepaidOptional that determine if the Credit Card dropdown and Prepaid options are shown
   * @param prepaidOptional tee_times_pre_paid_option on facility_tee_sheet_settings
   * @param prepaidRequired pre_paid_required on tee_times
   * @param creditCardRequired credit_card_required on tee_times
   * @param bookingFees booking_fee_required on cart
   * @param payNow payNow value on bookingState
   * @param paidGreenFees cart contains green fees with total_price > 0
   * @param bookingDeposit cart requires booking deposit
   * @returns
   */
  function getPaymentOptions(prepaidOptional: boolean, prepaidRequired: boolean, creditCardRequired: boolean, bookingFees: boolean, payNow: boolean, paidGreenFees: boolean, bookingDeposit: boolean) {
    let showPaymentDropdown = false;
    let showPrepaidOptional = false;

    //Show credit card dropdown if 
    //It's a prepaid tee time OR credit card required for hold OR bookings fees are present OR "Pay Now" is selected
    if ((prepaidRequired && paidGreenFees) || creditCardRequired || bookingFees || (prepaidOptional && payNow) || bookingDeposit) {
      showPaymentDropdown = true;
    }

    // Show "Pay Now" or "Pay at Course" options if 
    // tee_times_pre_paid_option value is true and its not a prepaid tee time and the cart has green fees with total_price > 0
    if (prepaidOptional && !prepaidRequired && paidGreenFees) {
      showPrepaidOptional = true;
    }

    return { showPaymentDropdown, showPrepaidOptional };
  }

  return {
    getDisabledBookings,
    getHomePageUrl,
    getURLParams,
    getPaymentOptions,
  };
}
